import React, { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { blogs } from '../data/blogsData';
import { FAQitem } from '../utils/FAQitem';


const batterySigns = [
    {
        id: 'sign-1',
        title: '1. The Engine Cranks Slower Than Usual',
        paragraphs: [
            'This is one of the most common early signs. If the engine sounds slower or more tired when it starts, the battery may not be delivering power the way it used to.',
            'A lot of drivers notice this first on cold mornings, but it can show up any time the battery starts getting weak.'
        ]
    },
    {
        id: 'sign-2',
        title: '2. You Hear Clicking When You Try to Start the Car',
        paragraphs: [
            'A clicking sound instead of a normal start is usually the car telling you there is not enough battery power to crank the engine properly.',
            'That does not always mean the battery is completely dead, but it is absolutely a sign you should stop ignoring.'
        ]
    },
    {
        id: 'sign-3',
        title: '3. The Battery Warning Light Comes On',
        paragraphs: [
            'If the battery warning light shows up on the dash, the electrical system wants attention. Sometimes it is the battery itself, and sometimes it is the alternator or charging system.',
            'Either way, it is not the kind of warning that should sit there for days while you hope it disappears.'
        ]
    },
    {
        id: 'sign-4',
        title: '4. Lights and Electronics Feel Weak',
        paragraphs: [
            'If the headlights look dim, the cabin lights fade while starting, or power windows and screens seem weaker than normal, the battery may be struggling.',
            'Electrical symptoms like this are often the early stage before a no-start problem shows up.'
        ]
    },
    {
        id: 'sign-5',
        title: '5. You Keep Needing Jump-Starts',
        paragraphs: [
            'Needing a jump once is already a sign to pay attention. Needing more than one jump usually means the problem is no longer a maybe.',
            'A healthy battery should not keep putting you in that situation unless something is wrong.'
        ]
    },
    {
        id: 'sign-6',
        title: '6. You See Corrosion on the Battery Terminals',
        paragraphs: [
            'White, blue, or crusty buildup around the terminals can interfere with the battery connection and make starting problems worse.',
            'Even if the battery itself is still okay, bad terminal corrosion can make the whole system act unreliable.'
        ]
    },
    {
        id: 'sign-7',
        title: '7. The Battery Case Looks Swollen or Damaged',
        paragraphs: [
            'A swollen battery case is a serious sign that heat, age, or charging issues may have already damaged the battery.',
            'If the battery looks physically deformed, cracked, or leaking, stop treating it like a small issue.'
        ]
    }
];

const faqs = [
    {
        question: 'How do I know if my car battery is getting weak?',
        answer:
            'Common signs include slow engine cranking, clicking when starting, dim lights, weak electronics, repeated jump-starts, and visible battery corrosion or swelling.'
    },
    {
        question: 'Can a bad battery still start the car sometimes?',
        answer:
            'Yes. A weak battery may still start the car occasionally, especially in mild weather, but it can fail suddenly once its condition gets worse.'
    },
    {
        question: 'Does the battery warning light always mean the battery is bad?',
        answer:
            'Not always. It can also point to a charging system issue, such as an alternator problem or poor electrical connection, but it should never be ignored.'
    },
    {
        question: 'Should I replace my battery before it completely dies?',
        answer:
            'Yes. If the battery is old and showing warning signs, replacing it early is usually better than waiting for a breakdown.'
    },
    {
        question: 'Can CarFido help me figure out if it is the battery?',
        answer:
            'Yes. CarFido can help you understand symptoms, think through likely causes, and decide what to check next.'
    }
];

const Blog8 = () => {
    const navigate = useNavigate();

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const blogId = 8;
    const blogData = blogs.find(b => b.id === blogId);

    return (
        <div className="bg-white">
            <div className="max-w-[1180px] mx-auto px-5 py-5 text-left">
                <button
                    onClick={() => navigate('/blogs')}
                    className="inline-flex fixed top-30 bg-black p-1 rounded-full hover:scale-110 items-center gap-2 text-gray-500 hover:text-gray-900 transition-colors text-sm font-medium mb-5"
                >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="19" y1="12" x2="5" y2="12"></line>
                        <polyline points="12 19 5 12 12 5"></polyline>
                    </svg>
                </button>
            </div>

            <div className="max-w-[1180px] mx-auto px-5 pb-[70px] flex flex-col lg:flex-row items-start gap-10">
                <main className="flex-1 max-w-full lg:max-w-[760px] min-w-0">
                    <section className="pb-6 border-b border-gray-200 mb-7">
                        <h1 className="text-3xl md:text-4xl lg:text-[42px] leading-[1.15] mb-3.5 text-gray-900 font-bold tracking-tight">
                            7 Warning Signs Your Car Battery May Be Dying
                        </h1>

                        <p className="text-lg md:text-xl text-gray-600 mb-[18px]">
                            Car batteries usually do not fail without warning. Most of the time, they give you a few signs first. The problem is that drivers often notice them and keep putting it off until the car refuses to start.
                        </p>

                        <div className="text-sm text-gray-500">
                            By <strong className="text-gray-900">CarFido Editorial Team</strong>
                            &nbsp;|&nbsp; Updated March 2026 &nbsp;|&nbsp; 8 min read
                        </div>
                    </section>

                    <figure className="mb-8">
                        <img
                            src="/blogs/8_1.jpeg"
                            alt="Car battery warning signs and early symptoms"
                            title="7 Warning Signs Your Car Battery May Be Dying"
                            className="w-full h-auto block border border-gray-300 rounded-xl"
                        />
                        <figcaption className="text-xs text-gray-400 mt-2">
                            Featured image: car_battery_warning_signs_featured.png
                        </figcaption>
                    </figure>

                    <nav className="bg-gray-50 border border-gray-200 rounded-xl p-5 mb-8">
                        <div className="text-base font-bold text-gray-900 mb-2.5">In this article</div>
                        <ul className="m-0 pl-[18px] text-gray-700 list-disc space-y-1">
                            <li><a href="#quick-answer" className="text-gray-900 no-underline hover:underline">Quick answer</a></li>
                            <li><a href="#warning-summary" className="text-gray-900 no-underline hover:underline">Warning summary</a></li>
                            <li><a href="#why-it-matters" className="text-gray-900 no-underline hover:underline">Why it matters</a></li>
                            <li><a href="#sign-1" className="text-gray-900 no-underline hover:underline">Slow crank</a></li>
                            <li><a href="#sign-2" className="text-gray-900 no-underline hover:underline">Clicking sound</a></li>
                            <li><a href="#sign-3" className="text-gray-900 no-underline hover:underline">Battery light</a></li>
                            <li><a href="#sign-4" className="text-gray-900 no-underline hover:underline">Weak electronics</a></li>
                            <li><a href="#sign-5" className="text-gray-900 no-underline hover:underline">Repeated jump-starts</a></li>
                            <li><a href="#sign-6" className="text-gray-900 no-underline hover:underline">Terminal corrosion</a></li>
                            <li><a href="#sign-7" className="text-gray-900 no-underline hover:underline">Battery swelling</a></li>
                            <li><a href="#what-to-do" className="text-gray-900 no-underline hover:underline">What to do next</a></li>
                            <li><a href="#faq" className="text-gray-900 no-underline hover:underline">FAQ</a></li>
                        </ul>
                    </nav>

                    <section className="mb-10 overflow-hidden">
                        <p className="mb-[18px] text-gray-700">
                            Battery problems have a habit of showing up at the worst time. One morning the car starts a little slower than usual, and a few days later you are stuck with a no-start problem and jumper cables in your hand.
                        </p>
                        <p className="mb-[18px] text-gray-700">
                            The good part is that a weak battery usually gives you clues before it dies completely. If you catch them early, you get to deal with it on your terms instead of wherever the car decides to quit.
                        </p>
                        <p className="mb-0 text-gray-700">
                            This guide covers the battery warning signs that are worth taking seriously before you get stranded.
                        </p>
                    </section>

                    <section id="quick-answer" className="mb-9 bg-gray-50 border-l-4 border-gray-900 rounded-lg p-[18px]">
                        <div className="text-sm font-bold text-gray-500 uppercase tracking-widest mb-2">Quick Answer</div>
                        <p className="m-0 text-gray-900 font-medium">
                            <strong>What are the signs of a dying car battery?</strong><br />
                            Common signs include slow cranking, clicking when starting, a battery warning light, dim lights, weak electronics, repeated jump-starts, terminal corrosion, and a swollen battery case.
                        </p>
                    </section>

                    <section id="warning-summary" className="mb-10">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">Battery Warning Summary</h2>
                        <div className="overflow-x-auto border border-gray-200 rounded-xl">
                            <table className="w-full border-collapse bg-white text-sm">
                                <thead className="bg-gray-50 text-gray-900 font-bold">
                                    <tr>
                                        <th className="text-left py-3.5 px-4 border-b border-gray-200">Sign</th>
                                        <th className="text-left py-3.5 px-4 border-b border-gray-200">Why It Matters</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-3.5 px-4 border-b border-gray-200 font-medium text-gray-900">Slow starting</td>
                                        <td className="py-3.5 px-4 border-b border-gray-200 text-gray-600">One of the earliest signs the battery is losing strength</td>
                                    </tr>
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-3.5 px-4 border-b border-gray-200 font-medium text-gray-900">Clicking or jump-starts</td>
                                        <td className="py-3.5 px-4 border-b border-gray-200 text-gray-600">Usually means the battery is struggling to provide enough power</td>
                                    </tr>
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-3.5 px-4 border-b border-gray-200 font-medium text-gray-900">Dim lights or weak electronics</td>
                                        <td className="py-3.5 px-4 border-b border-gray-200 text-gray-600">The electrical system may already be feeling the drop in voltage</td>
                                    </tr>
                                    <tr className="hover:bg-gray-50 transition-colors">
                                        <td className="py-3.5 px-4 font-medium text-gray-900 border-b border-gray-200">Corrosion or swelling</td>
                                        <td className="py-3.5 px-4 text-gray-600 border-b border-gray-200">Visible battery damage or poor terminal connection can lead to failure fast</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <section id="why-it-matters" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">Why It Matters</h2>
                        <p className="mb-4 text-gray-700">
                            A weak battery does not just mean the car might not start one day. It also means the rest of the electrical system may already be under more strain than it should be.
                        </p>
                        <p className="mb-5 text-gray-700">
                            Catching the early signs gives you time to test the battery, clean the terminals, or replace it before the problem turns into a stressful breakdown.
                        </p>
                        <ul className="pl-6 m-0 space-y-2 text-gray-700 list-disc">
                            <li>Prevents no-start surprises</li>
                            <li>Helps you catch charging issues earlier</li>
                            <li>Reduces the chance of getting stranded</li>
                            <li>Makes it easier to fix the problem before it grows</li>
                        </ul>
                    </section>

                    <figure className="mb-10">
                        <img
                            src="/blogs/8_2.jpeg"
                            alt="Signs of a weak car battery during engine start"
                            title="Slow start and weak battery signs"
                            className="w-full h-auto block border border-gray-300 rounded-xl"
                        />
                        <figcaption className="text-xs text-gray-400 mt-2">
                            Inline image: car_battery_slow_start_signs.png
                        </figcaption>
                    </figure>

                    {batterySigns.slice(0, 5).map((sign) => (
                        <section key={sign.id} id={sign.id} className="mb-11">
                            <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">
                                {sign.title}
                            </h2>
                            {sign.paragraphs.map((paragraph, index) => (
                                <p key={index} className="mb-4 text-gray-700">
                                    {paragraph}
                                </p>
                            ))}
                        </section>
                    ))}

                    <figure className="mb-10">
                        <img
                            src="/blogs/8_3.jpeg"
                            alt="Corrosion on a car battery terminal"
                            title="Battery corrosion and damage warning signs"
                            className="w-full h-auto block border border-gray-300 rounded-xl"
                        />
                        <figcaption className="text-xs text-gray-400 mt-2">
                            Inline image: car_battery_terminal_corrosion.png
                        </figcaption>
                    </figure>

                    {batterySigns.slice(5).map((sign) => (
                        <section key={sign.id} id={sign.id} className="mb-11">
                            <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">
                                {sign.title}
                            </h2>
                            {sign.paragraphs.map((paragraph, index) => (
                                <p key={index} className="mb-4 text-gray-700">
                                    {paragraph}
                                </p>
                            ))}
                        </section>
                    ))}

                    <section id="what-to-do" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">What To Do Next</h2>
                        <p className="mb-4 text-gray-700">
                            If the car has been showing two or three of these signs, do not keep treating it like a maybe. That is usually when you want to test the battery and charging system properly.
                        </p>
                        <ul className="pl-6 m-0 space-y-2 text-gray-700 list-disc">
                            <li>Check the battery age if you know it</li>
                            <li>Look for corrosion or loose connections</li>
                            <li>Get the battery and alternator tested</li>
                            <li>Replace the battery before it leaves you stranded</li>
                        </ul>
                    </section>

                    <section id="dash-section" className="mb-10 overflow-hidden">
                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                            <h2 className="text-2xl leading-tight mb-3.5 text-gray-900 font-bold">
                                Understand Battery Problems with CarFido
                            </h2>

                            <p className="mb-3.5 text-gray-700">
                                Battery issues are annoying mostly because the signs can feel random until the car finally refuses to start. CarFido helps you make more sense of the symptoms before it gets that far.
                            </p>

                            <p className="mb-3.5 text-gray-700">
                                You can use it to think through likely battery problems, charging issues, and what to check next before you start replacing parts blindly.
                            </p>

                            <p className="mb-2.5 text-gray-900 font-bold uppercase tracking-wide text-sm">You can ask questions such as:</p>

                            <ul className="pl-6 m-0 list-disc space-y-2 text-gray-700">
                                <li>Why is my car slow to start in the morning?</li>
                                <li>Does this clicking noise mean my battery is dying?</li>
                            </ul>
                        </div>
                    </section>

                    <section id="faq" className="mb-11 border-t border-gray-200 pt-8">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-5 text-gray-900 font-bold">Frequently Asked Questions</h2>
                        <div className="space-y-1">
                            {faqs.map((faq) => (
                                <FAQitem key={faq.question} question={faq.question} answer={faq.answer} />
                            ))}
                        </div>
                    </section>

                    <section id="final-thoughts" className="mb-11">
                        <h2 className="text-2xl md:text-3xl lg:text-[31px] leading-tight mb-4 text-gray-900 font-bold">Final Thoughts</h2>
                        <p className="mb-4 text-gray-700">
                            A dying battery often gives you a few warning signs before it completely fails. Slow starts, clicking noises, dim lights, and repeated jump-starts are all signs worth taking seriously.
                        </p>
                        <p className="mb-4 text-gray-700">
                            Catching those symptoms early gives you time to test the battery, clean connections, and replace it before the problem turns into a stressful emergency.
                        </p>
                        <p className="mb-0 text-gray-700">
                            If your car has been showing any of these signs, it is a good time to inspect the battery and act before it leaves you stranded.
                        </p>

                        <div className="mt-8 border border-gray-200 rounded-xl bg-gray-50 p-5 shadow-sm">
                            <div className="text-base text-gray-900 mb-2.5">
                                Next step: Check your battery's manufacture date. If it's more than 3-4 years old, consider a preventative test.
                            </div>
                        </div>
                    </section>
                </main>

                <aside className="w-full lg:w-[300px] lg:flex-[0_0_300px] lg:sticky lg:top-5 self-start space-y-5">
                    <div className="border border-gray-200 rounded-xl bg-white p-5 shadow-sm">
                        <div className="text-base font-bold text-gray-900 mb-3">Quick Navigation</div>
                        <ul className="list-none p-0 m-0 space-y-2.5">
                            <li><a href="#quick-answer" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Quick answer</a></li>
                            <li><a href="#warning-summary" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Warning summary</a></li>
                            <li><a href="#why-it-matters" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Why it matters</a></li>
                            <li><a href="#sign-1" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Slow crank</a></li>
                            <li><a href="#sign-2" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Clicking sound</a></li>
                            <li><a href="#sign-4" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Weak electronics</a></li>
                            <li><a href="#sign-6" className="no-underline text-gray-600 hover:text-gray-900 text-sm">Terminal corrosion</a></li>
                            <li><a href="#faq" className="no-underline text-gray-600 hover:text-gray-900 text-sm">FAQ</a></li>
                        </ul>
                    </div>

                    <div className="border border-gray-200 rounded-xl bg-gray-50 p-5 shadow-sm">
                        <div className="text-base font-bold text-gray-900 mb-2.5">Related Reads</div>
                        {(() => {
                            const otherBlogs = blogs.filter(b => b.id !== blogId);
                            const startIndex = blogId % otherBlogs.length;
                            return [...otherBlogs.slice(startIndex), ...otherBlogs.slice(0, startIndex)].slice(0, 3);
                        })().map((relatedBlog) => (
                            <Link
                                key={relatedBlog.id}
                                to={relatedBlog.path}
                                className="block no-underline text-gray-900 mb-3 text-sm leading-snug font-medium hover:text-gray-600 transition-colors"
                            >
                                {relatedBlog.title}
                            </Link>
                        ))}
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default Blog8;
